import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ERROR_KIND_ACCOUNT_DISABLED, isFixedInTheSpreadsheet, resolveErrorKind} from '../../channels/adAccountErrorKinds';
import {ReconnectAdAccountModal} from '../../agency/connections/ReconnectAdAccountModal';
import {ScreenTracker} from '../../../utils/ScreenTracker';
import {formatShortDate, ownerShort, reconnectPlatformName} from '../../agency/connections/reconnectAccountText';
import {supportsOAuthReconnect} from '../../agency/accounts_v2/accountsModel';

const RAW = {escapeValue: false};

// El aviso vive en una celda que cubre todas las columnas de la línea (miles de px) y arranca DESPUÉS de
// la columna fija del nombre. Sin anclarlo, sus botones quedaban más allá del borde de la tabla y, al
// desplazarla, el comienzo del texto pasaba por debajo de la columna fija (QA-VIS-rp-fe-61 y -81).
const STICKY_GAP = 16;
const STICKY_MAX_WIDTH = 896; // 56rem, lo que se lee cómodo en una línea

/** El ancestro que desplaza la tabla de costado (`.table-box.-dashboard`, `.portal-dashboard-table-scroll`). */
export function scrollParentOf(node) {
    let current = node ? node.parentElement : null;
    while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        if (/(auto|scroll)/.test(style.overflowX || style.overflow || '')) return current;
        current = current.parentElement;
    }
    return null;
}

/**
 * Dónde se pega el aviso: justo después de lo fijo de la fila (`stickyWidth`, lo que hay a la izquierda
 * de su celda) y no más ancho que lo que queda visible. `null` si no hay medida (jsdom, una tabla
 * oculta) o si no entra: entonces queda el estilo de la hoja.
 */
export function stickyPlacement(stickyWidth, scrollerWidth) {
    if (!(scrollerWidth > 0)) return null;
    const fixed = Math.max(stickyWidth, 0);
    const room = scrollerWidth - fixed - 2 * STICKY_GAP;
    if (room <= 0) return null;
    return {
        position: 'sticky',
        left: fixed + STICKY_GAP,
        maxWidth: Math.min(STICKY_MAX_WIDTH, room),
        marginLeft: 0,
        marginRight: 0,
    };
}

function useStickyPlacement(ref, active) {
    const [placement, setPlacement] = useState(null);
    useLayoutEffect(() => {
        const element = ref.current;
        if (!active || !element) return undefined;
        const cell = element.closest('td');
        const scroller = scrollParentOf(cell);
        if (!cell || !scroller) return undefined;
        const measure = () => {
            const left = cell.getBoundingClientRect().left - scroller.getBoundingClientRect().left
                + scroller.scrollLeft - scroller.clientLeft;
            setPlacement(stickyPlacement(left, scroller.clientWidth));
        };
        measure();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (observer) observer.observe(scroller);
        window.addEventListener('resize', measure);
        return () => {
            if (observer) observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [ref, active]);
    return placement;
}

// Conexiones › Cuentas con sólo las cuentas sin acceso de líneas activas (P-4 de la tanda).
export const UNREACHABLE_ACCOUNTS_PATH = '/agency/ad-accounts?acceso=sin';

/**
 * Las cuentas de la fila que perdieron el acceso (`account_status.connection` de `basic-data`), una por
 * cuenta. Las planillas no cuentan: no se arreglan reconectando, y su propia fila ya dice qué hacer. Una
 * cuenta cerrada por la plataforma (`ACCOUNT_DISABLED`) tampoco: no es acceso perdido (P-5), así que una
 * línea cuya única cuenta caída está cerrada dice «No pudimos actualizar…», sin botón.
 */
export function lostAccountsOf(adAccountsData) {
    const seen = new Set();
    const lost = [];
    (adAccountsData || []).forEach((row) => {
        const connection = row && row.account_status && row.account_status.connection;
        if (!connection) return;
        const kind = resolveErrorKind(connection.error_kind);
        if (isFixedInTheSpreadsheet(kind) || kind === ERROR_KIND_ACCOUNT_DISABLED) return;
        const key = connection.ad_account_pk != null ? `pk-${connection.ad_account_pk}` : `bucc-${row.id}`;
        if (seen.has(key)) return;
        seen.add(key);
        lost.push({row, connection});
    });
    return lost;
}

/** Lo que el modal de reconectar necesita de una cuenta del Tablero (el mismo armado que su celda). */
export function reconnectDataOf({row, connection}) {
    return {
        ad_account_id: connection.ad_account_pk,
        name: row.name,
        channel: row.channel,
        agency_channel_credential_label: connection.agency_channel_credential_label,
        error_found: connection.error_found,
        error_kind: resolveErrorKind(connection.error_kind),
        platform_user_name: connection.platform_user_name,
        connected_by_user_name: connection.connected_by_user_name,
        no_access_since_at: connection.no_access_since_at,
    };
}

/**
 * Qué dice la fila de una línea que no se actualiza hace mucho (M5, UD-21). `variant`:
 * - `legacy`: el backend no mandó `last_sync_at` (uno viejo): el texto de antes;
 * - `one`: perdimos el acceso a UNA cuenta → su nombre, quién la ve y «Reconectar» abre su modal;
 * - `many`: a varias → «Reconectar» lleva a Cuentas filtrado;
 * - `other`: no es de acceso, o es el portal del cliente (sin nombres ni botón).
 */
export function boardNoticeOf({syncInformation, adAccountsData, isClientView, language}) {
    const date = formatShortDate(syncInformation && syncInformation.last_sync_at, language);
    if (!date) return {variant: 'legacy'};
    if (isClientView) return {variant: 'other', date};
    const lost = lostAccountsOf(adAccountsData);
    if (lost.length === 1) return {variant: 'one', date, account: lost[0]};
    if (lost.length > 1) return {variant: 'many', date, count: lost.length};
    return {variant: 'other', date};
}

export function BoardSyncNotice({syncInformation, adAccountsData, isClientView, onShowRow}) {
    const {t, i18n} = useTranslation();
    const [reconnecting, setReconnecting] = useState(false);
    const notice = boardNoticeOf({syncInformation, adAccountsData, isClientView, language: i18n && i18n.language});
    const rootRef = useRef(null);
    const placement = useStickyPlacement(rootRef, notice.variant !== 'legacy');
    const account = notice.account || null;
    // Sólo donde el modal reconecta de verdad: pauta y GA4, el mismo criterio que la celda de la cuenta
    // (`BusinessUnitAdAccountNameCell._canReconnect`). Una tienda, Search Console o Escalafy caídas se
    // nombran igual, pero sin un botón que lleva a «La reconexión no está implementada» (C-rp-fe-02).
    const canReconnectOne = notice.variant === 'one' && account.connection.ad_account_pk != null
        && supportsOAuthReconnect(account.row.channel);
    const hasButton = canReconnectOne || notice.variant === 'many';

    // `board_reconnect_shown`: una vez por fila montada (`UserScreenActivity` lo cuenta por día).
    const shownRef = useRef(false);
    useEffect(() => {
        if (!hasButton || shownRef.current) return;
        shownRef.current = true;
        ScreenTracker.recordEvent('board_reconnect_shown');
    }, [hasButton]);

    // Un botón con cara de link: no navega a ningún lado (antes era un `<a href="#">`).
    const showRow = (
        <button type="button" className="board-sync-notice__show" onClick={onShowRow}>
            {t('dashboard_row.want_to_see_row_anyway')}
        </button>
    );

    if (notice.variant === 'legacy') {
        return (
            <p className="loader-message">
                {t('dashboard_row.connection_problem_last_sync', {lastSync: syncInformation && syncInformation.last_sync})}
                {' '}{showRow}
            </p>
        );
    }

    let text;
    let owner = null;
    if (notice.variant === 'one') {
        text = t('dashboard_row.lost_access_one', {
            date: notice.date,
            account: account.row.name || account.row.account_id,
            platform: reconnectPlatformName(account.row.channel),
            interpolation: RAW,
        });
        owner = ownerShort(t, account.connection);
    } else if (notice.variant === 'many') {
        text = t('dashboard_row.lost_access_many', {date: notice.date, count: notice.count, interpolation: RAW});
    } else {
        text = t('dashboard_row.update_failed_since', {date: notice.date, interpolation: RAW});
    }

    const clicked = () => ScreenTracker.recordEvent('board_reconnect_click');

    return (
        <div className="board-sync-notice" data-testid="board-sync-notice" data-variant={notice.variant}
             ref={rootRef} style={placement || undefined}>
            <p className="board-sync-notice__text">
                <span>{text}</span>
                {owner && <span className="board-sync-notice__owner">{owner}</span>}
            </p>
            <div className="board-sync-notice__actions">
                {canReconnectOne && (
                    <button type="button" className="btn -alt board-sync-notice__btn"
                            onClick={() => { clicked(); setReconnecting(true); }}>
                        {t('reconnect_modal.reconnect')}
                    </button>
                )}
                {notice.variant === 'many' && (
                    <Link className="btn -alt board-sync-notice__btn" to={UNREACHABLE_ACCOUNTS_PATH} onClick={clicked}>
                        {t('reconnect_modal.reconnect')}
                    </Link>
                )}
                {showRow}
            </div>
            {reconnecting && (
                <ReconnectAdAccountModal isOpen={true} onToggle={() => setReconnecting(false)}
                                         adAccountData={reconnectDataOf(account)}
                                         onReconnected={() => window.location.reload()}/>
            )}
        </div>
    );
}

export default BoardSyncNotice;
